import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import MainLayout from "../../components/layout/MainLayout";
import CommonActionMenu from "../../components/common/CommonActionMenu";
import CommonTable from "../../components/common/CommonTable";
import { useAuth } from "../../contexts/AuthContext";
import api from "../../services/api";
import Swal from "sweetalert2";
import { hasPermission } from "../../utils/permissions";
import "./Payroll.css";

function ViewPayroll() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [payrolls, setPayrolls] = useState([]);
  const [downloadingPayslip, setDownloadingPayslip] = useState(null);

  useEffect(() => {
    fetchPayrolls();
  }, []);

  const fetchPayrolls = async () => {
    try {
      const response = await api.get("/payroll/view");
      if (response.data.success) {
        setPayrolls(response.data.data);
      }
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "Failed to load payrolls",
      });
    } finally {
      setLoading(false);
    }
  };

  const formatNumber = (num) =>
    new Intl.NumberFormat("en-IN", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(num || 0);

  const formatCurrency = (num) => `Rs ${formatNumber(num)}`;

  const getCurrentFYPayrolls = () => {
    const now = new Date();
    const fyStartYear =
      now.getMonth() >= 3 ? now.getFullYear() : now.getFullYear() - 1;
    const fyEndYear = fyStartYear + 1;

    const fyMonths = [
      "April",
      "May",
      "June",
      "July",
      "August",
      "September",
      "October",
      "November",
      "December",
      "January",
      "February",
      "March",
    ];

    return payrolls.filter((payroll) => {
      const year = Number.parseInt(payroll.year, 10);
      const monthIndex = fyMonths.indexOf(payroll.month);

      if (monthIndex === -1) {
        return false;
      }

      const belongsToYear = monthIndex <= 8 ? fyStartYear : fyEndYear;
      return year === belongsToYear;
    });
  };

  const getFYStats = () => {
    const fyPayrolls = getCurrentFYPayrolls();

    return fyPayrolls.reduce(
      (accumulator, payroll) => {
        accumulator.netPay += payroll.total_netpay || 0;
        accumulator.employees = payroll.emp_count || 0;
        accumulator.tds += payroll.tenetive_tds || 0;
        accumulator.epf += payroll.total_epf || 0;
        accumulator.esi += payroll.total_esi || 0;
        return accumulator;
      },
      { netPay: 0, employees: 0, tds: 0, epf: 0, esi: 0 },
    );
  };

  const handleViewDetails = (payroll) => {
    navigate(`/payroll/details/${payroll.id}`, {
      state: {
        payrollId: payroll.id,
        month: payroll.month,
        year: payroll.year,
      },
    });
  };

  const handleOpenPayrollRun = (payroll) => {
    handleViewDetails(payroll);
  };

  const handleDownloadPayslips = async (payroll) => {
    setDownloadingPayslip(payroll.id);

    try {
      const res = await api.get(`/payroll/details/${payroll.id}`);

      if (!res.data.success) {
        Swal.fire({
          icon: "error",
          title: "Error",
          text: "Could not load payroll details",
        });
        return;
      }

      const employees = res.data.data.employees;

      if (!employees || employees.length === 0) {
        Swal.fire({
          icon: "warning",
          title: "No Employees",
          text: "No employees found in this payroll.",
        });
        return;
      }

      const inputOptions = {};
      employees.forEach((employee) => {
        inputOptions[employee.code] = `${employee.name} (${employee.code})`;
      });

      const { value: selectedCode } = await Swal.fire({
        title: "Download Payslip",
        html: `<p style="margin-bottom:8px;font-size:13px;color:#666;">${payroll.month} ${payroll.year} &middot; ${employees.length} employees</p>`,
        input: "select",
        inputOptions,
        inputPlaceholder: "Select an employee",
        showCancelButton: true,
        confirmButtonText: "Download",
        confirmButtonColor: "#6557ff",
        inputValidator: (value) => {
          if (!value) {
            return "Please select an employee";
          }
          return undefined;
        },
      });

      if (!selectedCode) {
        return;
      }

      const selectedEmployee = employees.find((employee) => employee.code === selectedCode);

      await downloadPayslipForEmployee(
        payroll.id,
        selectedCode,
        payroll.month,
        payroll.year,
        selectedEmployee,
      );
    } catch (error) {
      console.error(error);
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "Failed to load employee list",
      });
    } finally {
      setDownloadingPayslip(null);
    }
  };

  const downloadPayslipForEmployee = async (
    payrollId,
    employeeCode,
    month,
    year,
    employee = null,
  ) => {
    try {
      const response = await api.get(
        `/payroll/download-payslip/${payrollId}/${employeeCode}`,
        { responseType: "blob" },
      );

      const contentType = response.headers?.["content-type"] || "";
      if (!contentType.includes("application/pdf")) {
        const errorText = await response.data.text();
        let message = "Could not generate payslip PDF.";

        try {
          const parsed = JSON.parse(errorText);
          message = parsed.message || message;
        } catch (parseError) {
          if (errorText) {
            message = errorText;
          }
        }

        throw new Error(message);
      }

      const blob = new Blob([response.data], { type: "application/pdf" });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      const rawName = employee?.name || "Employee";
      const safeName = rawName
        .replace(/[^A-Za-z0-9]+/g, "_")
        .replace(/^_+|_+$/g, "") || "Employee";
      link.href = url;
      link.download = `Payslip_${safeName}_${employeeCode}_${month}_${year}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error(error);
      Swal.fire({
        icon: "error",
        title: "Download Failed",
        text: error.message || "Could not generate payslip PDF.",
      });
    }
  };

  if (loading) {
    return (
      <MainLayout>
        <div className="loading-container">
          <div className="spinner"></div>
          <p>Loading payrolls...</p>
        </div>
      </MainLayout>
    );
  }

  const canGeneratePayroll = hasPermission(user, "payroll.generate");
  const fyStats = getFYStats();
  const payrollColumns = [
    {
      header: "Sr. No.",
      render: (_, index) => index + 1,
    },
    {
      header: "Month (Year)",
      render: (payroll) => (
        <div className="month-badge">
          <span className="month">{payroll.month}</span>
          <span className="year">({payroll.year})</span>
          {payroll.run_sequence > 1 && (
            <span className="month-badge-meta">{`Run ${payroll.run_sequence}`}</span>
          )}
          {payroll.engine_version_label && (
            <span className="month-badge-meta">{payroll.engine_version_label}</span>
          )}
        </div>
      ),
    },
    {
      header: "Employees",
      headerClassName: "text-center",
      cellClassName: "text-center",
      render: (payroll) => (
        <span className="badge badge-primary">{payroll.emp_count}</span>
      ),
    },
    {
      header: "Gross Pay",
      headerClassName: "text-right",
      cellClassName: "text-right",
      render: (payroll) => formatCurrency(payroll.total_grosspay),
    },
    {
      header: "Eff. Gross",
      headerClassName: "text-right",
      cellClassName: "text-right",
      render: (payroll) => formatCurrency(payroll.effective_gross),
    },
    {
      header: "Net Pay",
      headerClassName: "text-right",
      cellClassName: "text-right net-pay",
      render: (payroll) => formatCurrency(payroll.total_netpay),
    },
    {
      header: "TDS",
      headerClassName: "text-right",
      cellClassName: "text-right",
      render: (payroll) => formatCurrency(payroll.tenetive_tds),
    },
    {
      header: "Prof. Tax",
      headerClassName: "text-right",
      cellClassName: "text-right",
      render: (payroll) => formatCurrency(payroll.monthly_prof_tax),
    },
    {
      header: "Action",
      headerClassName: "text-center",
      cellClassName: "text-center",
      render: (payroll) => (
        <CommonActionMenu
          ariaLabel={`Actions for payroll ${payroll.month} ${payroll.year}`}
          actions={[
            {
              label: "View Details",
              icon: "fas fa-eye",
              onClick: () => handleViewDetails(payroll),
            },
            {
              label: "Open Payroll Run",
              icon: "fas fa-folder-open",
              variant: "warning",
              onClick: () => handleOpenPayrollRun(payroll),
            },
            {
              label:
                downloadingPayslip === payroll.id
                  ? "Loading Payslips..."
                  : "Download Payslip",
              icon:
                downloadingPayslip === payroll.id
                  ? "fas fa-spinner fa-spin"
                  : "fas fa-file-invoice",
              variant: "success",
              disabled: downloadingPayslip === payroll.id,
              onClick: () => handleDownloadPayslips(payroll),
            },
          ]}
        />
      ),
    },
  ];

  return (
    <MainLayout>
      <div className="view-payroll-page">
        <div className="page-header-section">
          <div>
            <h1>View Payroll</h1>
            <p className="subtitle">
              All submitted payrolls and salary reports
            </p>
          </div>
          <div className="header-actions">
            {canGeneratePayroll && (
              <button
                className="btn-primary"
                onClick={() => navigate("/payroll/generate")}
              >
                <i className="fas fa-plus"></i> New Payroll
              </button>
            )}
          </div>
        </div>

        <div className="summary-cards">
          <div className="summary-card">
            <div className="card-icon primary">
              <i className="fas fa-wallet"></i>
            </div>
            <div className="card-content">
              <h3>{formatCurrency(fyStats.netPay)}</h3>
              <p>Total Net Pay (This FY)</p>
            </div>
          </div>
          <div className="summary-card">
            <div className="card-icon warning">
              <i className="fas fa-receipt"></i>
            </div>
            <div className="card-content">
              <h3>{formatCurrency(fyStats.tds)}</h3>
              <p>Total TDS (This FY)</p>
            </div>
          </div>
          <div className="summary-card">
            <div className="card-icon info">
              <i className="fas fa-shield-alt"></i>
            </div>
            <div className="card-content">
              <h3>{formatCurrency(fyStats.epf)}</h3>
              <p>Total EPF (This FY)</p>
            </div>
          </div>
          <div className="summary-card">
            <div className="card-icon danger">
              <i className="fas fa-heartbeat"></i>
            </div>
            <div className="card-content">
              <h3>{formatCurrency(fyStats.esi)}</h3>
              <p>Total ESI (This FY)</p>
            </div>
          </div>
        </div>

        <div className="table-card payroll-records-card">
          <div className="table-header payroll-records-header">
            <div className="payroll-records-title">
              <span className="payroll-records-icon" aria-hidden="true">
                <i className="fas fa-file-invoice-dollar"></i>
              </span>
              <div>
                <h5>Payroll Records</h5>
                <p>Submitted monthly payroll runs</p>
              </div>
            </div>
            <span className="record-count">{payrolls.length} records</span>
          </div>

          {payrolls.length === 0 ? (
            <div className="empty-state">
              <i className="fas fa-folder-open"></i>
              <p>No payrolls found</p>
              {canGeneratePayroll && (
                <button
                  className="btn-primary"
                  onClick={() => navigate("/payroll/generate")}
                >
                  Generate First Payroll
                </button>
              )}
            </div>
          ) : (
            <CommonTable
              columns={payrollColumns}
              data={payrolls}
              tableClassName="payroll-table payroll-records-table"
              wrapperClassName="table-responsive payroll-records-table-wrap"
              toolbarClassName="common-table-toolbar payroll-records-toolbar"
              entriesClassName="common-table-entries payroll-records-entries"
              searchBoxClassName="common-table-search payroll-records-search"
              paginationClassName="common-table-pagination payroll-records-pagination"
              searchPlaceholder="Search by month or year..."
              getSearchText={(payroll) =>
                [payroll.month, payroll.year].filter(Boolean).join(" ")
              }
            />
          )}
        </div>
      </div>
    </MainLayout>
  );
}

export default ViewPayroll;
